import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import {
  FaEnvelope,
  FaExclamationTriangle,
  FaLock,
  FaSignInAlt,
  FaUserPlus,
} from "react-icons/fa";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();

  const [mode, setMode] = useState("signin");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [isRecoveryMode, setIsRecoveryMode] = useState(false);

  const [form, setForm] = useState({
    email: "",
    password: "",
    confirmPassword: "",
  });

  const redirectToUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    return `${window.location.origin}/login`;
  }, []);

  useEffect(() => {
    let mounted = true;

    const detectRecoveryState = async () => {
      const hash = window.location.hash || "";
      const search = window.location.search || "";

      const hashParams = new URLSearchParams(hash.replace(/^#/, ""));
      const searchParams = new URLSearchParams(search);

      const hashType = hashParams.get("type");
      const searchType = searchParams.get("type");

      const isRecovery =
        hashType === "recovery" ||
        searchType === "recovery" ||
        hash.includes("type=recovery");

      if (isRecovery && mounted) {
        setIsRecoveryMode(true);
        setMode("recovery");
        setMessage("Enter your new password below to reset your account password.");
        setError("");
        return;
      }

      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!mounted) return;

      if (session && !isRecovery) {
        navigate("/home", { replace: true });
      }
    };

    detectRecoveryState();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (!mounted) return;

      if (event === "PASSWORD_RECOVERY") {
        setIsRecoveryMode(true);
        setMode("recovery");
        setMessage("Enter your new password below to reset your account password.");
        setError("");
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetUiState = (nextMode) => {
    setMode(nextMode);
    setError("");
    setMessage("");
    setForm((prev) => ({
      ...prev,
      password: "",
      confirmPassword: "",
    }));
  };

  const handleSignIn = async () => {
    const email = form.email.trim().toLowerCase();
    const password = form.password;

    if (!email || !password) {
      setError("Please enter your email and password.");
      return;
    }

    setLoading(true);
    setError("");
    setMessage("");

    const { error: signInError } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (signInError) {
      setError(signInError.message || "Could not sign in.");
      setLoading(false);
      return;
    }

    const redirectTo = location.state?.from?.pathname || "/home";
    navigate(redirectTo, { replace: true });
    setLoading(false);
  };

  const handleSignUp = async () => {
    const email = form.email.trim().toLowerCase();
    const password = form.password;
    const confirmPassword = form.confirmPassword;

    if (!email || !password || !confirmPassword) {
      setError("Please complete all signup fields.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    setLoading(true);
    setError("");
    setMessage("");

    const { error: signUpError } = await supabase.auth.signUp({
      email,
      password,
    });

    if (signUpError) {
      console.error("Supabase signup error:", signUpError);
      setError(
        signUpError.message ||
          signUpError.code ||
          "Could not create account.",
      );
      setLoading(false);
      return;
    }

    setMessage(
      "Account created. Head to the login section, and use your details to login.",
    );
    setMode("signin");
    setForm({
      email,
      password: "",
      confirmPassword: "",
    });
    setLoading(false);
  };

  const handleForgotPassword = async () => {
    const email = form.email.trim().toLowerCase();

    if (!email) {
      setError("Enter your email address first.");
      return;
    }

    setLoading(true);
    setError("");
    setMessage("");

    const { error: resetError } = await supabase.auth.resetPasswordForEmail(
      email,
      {
        redirectTo: redirectToUrl,
      },
    );

    if (resetError) {
      setError(resetError.message || "Could not send password reset email.");
      setLoading(false);
      return;
    }

    setMessage(
      "Password reset email sent. Check your inbox and open the reset link.",
    );
    setLoading(false);
  };

  const handleUpdatePassword = async () => {
    const password = form.password;
    const confirmPassword = form.confirmPassword;

    if (!password || !confirmPassword) {
      setError("Please enter and confirm your new password.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    setLoading(true);
    setError("");
    setMessage("");

    const { error: updateError } = await supabase.auth.updateUser({
      password,
    });

    if (updateError) {
      setError(updateError.message || "Could not update password.");
      setLoading(false);
      return;
    }

    setMessage("Password updated successfully. You can now sign in.");
    setIsRecoveryMode(false);
    setMode("signin");
    setForm({
      email: "",
      password: "",
      confirmPassword: "",
    });

    if (typeof window !== "undefined") {
      window.history.replaceState({}, document.title, "/login");
    }

    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isRecoveryMode || mode === "recovery") {
      await handleUpdatePassword();
      return;
    }

    if (mode === "signin") {
      await handleSignIn();
      return;
    }

    await handleSignUp();
  };

  return (
    <div className="min-h-screen bg-[#08111f] text-white">
      <div className="pointer-events-none fixed inset-0">
        <div className="absolute inset-0 bg-[linear-gradient(135deg,#4a0454_0%,#3c2f73_35%,#255a86_68%,#12728a_100%)] opacity-90" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.08),transparent_26%),radial-gradient(circle_at_bottom_right,rgba(255,255,255,0.05),transparent_24%)]" />
        <div className="absolute inset-0 bg-black/30" />
      </div>

      <div className="relative mx-auto flex min-h-screen max-w-[1600px] items-center justify-center px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid w-full max-w-6xl overflow-hidden rounded-[32px] border border-white/10 bg-[rgba(17,26,43,0.72)] shadow-[0_20px_80px_rgba(0,0,0,0.32)] backdrop-blur-xl lg:grid-cols-[1.05fr_0.95fr]">
          <div className="border-b border-white/10 p-8 lg:border-b-0 lg:border-r lg:p-12">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-[11px] font-medium uppercase tracking-[0.24em] text-white/80">
              {isRecoveryMode || mode === "recovery" ? (
                <FaLock />
              ) : mode === "signin" ? (
                <FaSignInAlt />
              ) : (
                <FaUserPlus />
              )}
              <span>
                {isRecoveryMode || mode === "recovery"
                  ? "Reset Password"
                  : mode === "signin"
                    ? "Sign In"
                    : "Create Account"}
              </span>
            </div>

            <h1 className="text-3xl font-semibold tracking-tight sm:text-5xl">
              Benton Theatres
            </h1>

            <p className="mt-4 max-w-2xl text-sm leading-7 text-white/80 sm:text-base lg:text-lg">
              {isRecoveryMode || mode === "recovery"
                ? "Set a new password for your Benton account."
                : mode === "signin"
                  ? "Sign in with your Benton account to access protected areas."
                  : "Create an account to access protected areas. Admin access is still controlled through your profiles table."}
            </p>

            {!isRecoveryMode && mode !== "recovery" ? (
              <div className="mt-8 grid gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => resetUiState("signin")}
                  className={`rounded-2xl border px-4 py-3 text-sm font-semibold transition ${
                    mode === "signin"
                      ? "border-cyan-300/40 bg-cyan-400/10 text-cyan-100"
                      : "border-white/10 bg-white/[0.06] text-white/80 hover:bg-white/[0.09]"
                  }`}
                >
                  Sign In
                </button>

                <button
                  type="button"
                  onClick={() => resetUiState("signup")}
                  className={`rounded-2xl border px-4 py-3 text-sm font-semibold transition ${
                    mode === "signup"
                      ? "border-cyan-300/40 bg-cyan-400/10 text-cyan-100"
                      : "border-white/10 bg-white/[0.06] text-white/80 hover:bg-white/[0.09]"
                  }`}
                >
                  Sign Up
                </button>
              </div>
            ) : null}
          </div>

          <div className="p-8 lg:p-12">
            <form onSubmit={handleSubmit} className="space-y-4">
              {error ? (
                <div className="flex items-start gap-3 rounded-2xl border border-red-400/20 bg-red-500/10 px-4 py-4 text-sm text-red-100">
                  <FaExclamationTriangle className="mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              ) : null}

              {message ? (
                <div className="rounded-2xl border border-emerald-400/20 bg-emerald-500/10 px-4 py-4 text-sm text-emerald-100">
                  {message}
                </div>
              ) : null}

              {!isRecoveryMode && mode !== "recovery" ? (
                <div>
                  <label className="mb-2 block text-sm font-medium text-white/82">
                    Email
                  </label>
                  <div className="relative">
                    <FaEnvelope className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleInputChange}
                      placeholder="Enter your email"
                      className="h-12 w-full rounded-2xl border border-white/10 bg-white/[0.08] pl-12 pr-4 text-sm text-white outline-none transition placeholder:text-white/35 focus:border-cyan-300/40"
                    />
                  </div>
                </div>
              ) : null}

              <div>
                <label className="mb-2 block text-sm font-medium text-white/82">
                  {isRecoveryMode || mode === "recovery"
                    ? "New Password"
                    : "Password"}
                </label>
                <div className="relative">
                  <FaLock className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                  <input
                    type="password"
                    name="password"
                    value={form.password}
                    onChange={handleInputChange}
                    placeholder={
                      isRecoveryMode || mode === "recovery"
                        ? "Enter your new password"
                        : "Enter your password"
                    }
                    className="h-12 w-full rounded-2xl border border-white/10 bg-white/[0.08] pl-12 pr-4 text-sm text-white outline-none transition placeholder:text-white/35 focus:border-cyan-300/40"
                  />
                </div>
              </div>

              {mode === "signup" || isRecoveryMode || mode === "recovery" ? (
                <div>
                  <label className="mb-2 block text-sm font-medium text-white/82">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <FaLock className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                    <input
                      type="password"
                      name="confirmPassword"
                      value={form.confirmPassword}
                      onChange={handleInputChange}
                      placeholder="Confirm your password"
                      className="h-12 w-full rounded-2xl border border-white/10 bg-white/[0.08] pl-12 pr-4 text-sm text-white outline-none transition placeholder:text-white/35 focus:border-cyan-300/40"
                    />
                  </div>
                </div>
              ) : null}

              {mode === "signin" && !isRecoveryMode ? (
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  disabled={loading}
                  className="text-sm font-medium text-cyan-100 underline underline-offset-4"
                >
                  Forgot Password?
                </button>
              ) : null}

              <button
                type="submit"
                disabled={loading}
                className="inline-flex h-12 w-full items-center justify-center rounded-2xl bg-[linear-gradient(90deg,#6a1b9a,#3c67d6,#1aa4b8)] px-5 text-sm font-semibold text-white shadow-[0_12px_32px_rgba(26,164,184,0.25)] transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading
                  ? isRecoveryMode || mode === "recovery"
                    ? "Updating password..."
                    : mode === "signin"
                      ? "Signing in..."
                      : "Creating account..."
                  : isRecoveryMode || mode === "recovery"
                    ? "Update Password"
                    : mode === "signin"
                      ? "Sign In"
                      : "Create Account"}
              </button>
            </form>

            <div className="mt-6 text-sm text-white/60">
              Back to{" "}
              <Link
                to="/home"
                className="font-medium text-white underline underline-offset-4"
              >
                Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}